/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import { type fabric } from 'fabric';
import { svgToIcon, svgToURL } from '../utils/svg';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { type TextProps } from '../objects/EmbeddedTextBehaviorsV2';
import { type ToolList } from 'fabric/ObjectBehaviors';
import { upperFirst } from 'lodash';
import browserStandard from '../assets/icons/deviceBrowserStandard.svg';
import CanvaSXBehaviors from './CanvaSXBehaviors';
import laptopBrowserBar from '../assets/devices/laptopBrowserBar.svg';
import laptopComponent from '../assets/devices/laptop.svg';
import laptopStandard from '../assets/icons/deviceLaptopStandard.svg';
import phoneStandard from '../assets/icons/devicePhoneStandard.svg';
import tabletStandard from '../assets/icons/deviceTabletStandard.svg';
import tools from '../tools';
import ToolLayoutAlignment from '../tools/ToolLayoutAlignment';
import ToolLayoutOrientation from '../tools/ToolLayoutOrientation';
import ToolLayoutSizeSpacing from '../tools/ToolLayoutSizeSpacing';
import ConnectorAttachmentBehaviors from './ConnectorAttachmentBehaviors';
import { AttachmentPoint, type Resizable } from '../types';
import { FlexColumn } from '@lib/canvasx/components/FlexColumn';
import { Image } from '@lib/canvasx/components/Image';
import { Rect } from '@lib/canvasx/components/Rect';
import { BrowserStatusBar } from './BrowserStatusBar';
import { MobileStatusBar } from './MobileStatusBar';
import LinearResizeControl from '../controls/LinearResizeControl';
import CommentableBehaviors from './CommentableBehaviors';
import LockBehaviors from './LockBehaviors';
import ShapeBase from '../shapes/ShapeBase';
import LayoutBehaviors from './LayoutBehaviors';
import FrameBehaviors, { HOVER_SHADOW_COLOR } from './FrameBehaviors';
import Frame from './Frame';

const SVG_COMPONENTS = {
  laptop: laptopComponent,
  laptopBrowserBar,
} as const;

const DEVICE_DIMENSIONS = {
  phone: { width: 300, height: 645 },
  tablet: { width: 600, height: 800 },
  browser: { width: 1264, height: 920 },
  laptop: { width: 1264, height: 920 },
};

const DEVICE_STANDARD_ICONS = {
  phone: phoneStandard,
  tablet: tabletStandard,
  browser: browserStandard,
  laptop: laptopStandard,
};

export const MOBILE_DEVICES = ['phone', 'tablet'];

interface Device
  extends FrameBehaviors,
    CanvaSXBehaviors,
    ConnectorAttachmentBehaviors,
    LockBehaviors,
    CommentableBehaviors,
    LayoutBehaviors {}

@LayoutBehaviors
@FrameBehaviors
@CanvaSXBehaviors
@ConnectorAttachmentBehaviors
@LockBehaviors
@CommentableBehaviors
class Device extends Frame implements Resizable {
  type = 'device';
  noScaleCache = false;
  objectCaching = false;
  selectionFullyContained = true;
  minScaleLimit = 0.1;
  // Whether to skip the frame in the list of frames to scroll through
  notInNavigation = false;
  uniformScaling = true;

  deviceName: string;
  orientation: 'portrait' | 'landscape';

  // Style flag option
  showBrowserBar: boolean;
  showFramed: boolean;
  showStatusBar: boolean;

  textProps: TextProps = {
    allowedStyles: 'simple',
    location: 'above',
    textAlign: 'left',
    verticalAlign: 'center',
    fontSize: 'fixed',
    overflow: 'visible',
    wrapText: 'growText',
    margins: 'noScale',
  };

  static styleFlags = ['showBrowserBar', 'showFramed', 'showStatusBar'];
  // Keys that will be serialized for saving and sending to remote systems.
  serializedKeys = [
    'z',
    'textContent',
    'left',
    'top',
    'width',
    'height',
    'scaleX',
    'scaleY',
    'fill',
    'stroke',
    'strokeWidth',
    'opacity',
    'container',
    'layoutPosition',
    'layoutManual',

    // device specific
    'deviceName',
    'orientation',

    ...Device.styleFlags,
  ];

  device: any;

  controls = {
    tl: ShapeBase.resizeControl({ x: -0.5, y: -0.5 }),
    tr: ShapeBase.resizeControl({ x: 0.5, y: -0.5 }),
    bl: ShapeBase.resizeControl({ x: -0.5, y: 0.5 }),
    br: ShapeBase.resizeControl({ x: 0.5, y: 0.5 }),
    mt: new LinearResizeControl({ edge: 'top' }),
    mb: new LinearResizeControl({ edge: 'bottom' }),
    ml: new LinearResizeControl({ edge: 'left' }),
    mr: new LinearResizeControl({ edge: 'right' }),
  };

  constructor(options) {
    // Set dimensions from DEVICE_DIMENSIONS before calling super
    if (options.deviceName && (!options.width || !options.height)) {
      const { width, height } = Device.deviceDimensions(
        options.deviceName,
        options.orientation
      );

      options.width ||= width;
      options.height ||= height;
    }

    super(options);

    this.initializeConnectorAttachments();
    this.initializeDevice(options.deviceName);
    this.initializeLockBehaviors(options.locked);
    this.initializeLayout();

    // Backwards compat: added the "flag" prefix to the style keys
    if (options.framed) {
      this.set('showFramed', true);
      this.set('framed', null);
    }
    if (options.statusBar) {
      this.set('showStatusBar', true);
      this.set('statusBar', null);
    }
    if (options.browserBar) {
      this.set('showBrowserBar', true);
      this.set('browserBar', null);
    }
  }

  initializeDevice(newDeviceName) {
    const { width: currentWidth, height: currentHeight } = this;
    const { width: deviceWidth, height: deviceHeight } =
      this.deviceDimensions(newDeviceName);

    let width = Math.max(deviceWidth, currentWidth);
    let height = Math.max(deviceHeight, currentHeight);
    if (newDeviceName === 'laptop') {
      // Laptops are not resizable and must adhere to fixed dimensions
      width = deviceWidth;
      height = deviceHeight;
    }

    // Hide resize controls for laptop, which is only uniformaly scalable
    ['mt', 'mb', 'ml', 'mr'].forEach(control => {
      if (this.controls[control]) {
        this.controls[control].setVisibility(newDeviceName !== 'laptop');
      }
    });

    this.set({ width, height });

    // Update connector attachments to move them to the right place.
    this.synchronize();
  }

  updateOrientation(orientation) {
    const currentOrientation = this.orientation;
    if (orientation !== currentOrientation) {
      this.set({
        width: this.height,
        height: this.width,
      });
    }
  }

  static possibleAttachmentPoints: AttachmentPoint[] = Object.keys(
    Device.connectControlDefinition()
  ) as AttachmentPoint[];

  attachmentPoints(): AttachmentPoint[] {
    // @ts-expect-error TS doesn't like accessing constructor like this
    return this.constructor.possibleAttachmentPoints;
  }

  connectControlDefinition() {
    // @ts-expect-error TS doesn't like accessing constructor like this
    return this.constructor.connectControlDefinition();
  }

  static connectControlDefinition() {
    return {
      cl: {
        x: -0.5,
        y: 0,
        normal: { x: -1, y: 0 },
        oppositeAttachmentPoint: AttachmentPoint.Right,
      },
      cr: {
        x: 0.5,
        y: 0,
        normal: { x: 1, y: 0 },
        oppositeAttachmentPoint: AttachmentPoint.Left,
      },
      ct: {
        x: 0,
        y: -0.5,
        normal: { x: 0, y: -1 },
        oppositeAttachmentPoint: AttachmentPoint.Bottom,
      },
      cb: {
        x: 0,
        y: 0.5,
        normal: { x: 0, y: 1 },
        oppositeAttachmentPoint: AttachmentPoint.Top,
      },
    };
  }

  tools() {
    const deviceTools: ToolList = [[tools.ToolDeviceType]];
    const layoutTools: ToolList = [tools.ToolLayoutEnable];
    if (['phone', 'tablet'].includes(this.deviceName)) {
      deviceTools.push([tools.ToolDevicePhoneTabletStyleFlags]);
    }
    if (this.deviceName === 'laptop') {
      deviceTools.push([tools.ToolDeviceLaptopStyleFlags]);
    }

    if (MOBILE_DEVICES.includes(this.deviceName)) {
      deviceTools.push([tools.ToolDeviceOrientation, tools.ToolSize]);
    } else {
      deviceTools.push([tools.ToolSize]);
    }

    if (this.layoutEnabled) {
      layoutTools.push(
        ToolLayoutAlignment,
        ToolLayoutOrientation,
        ToolLayoutSizeSpacing
      );
    }

    deviceTools.push([tools.ToolFillColor], layoutTools);

    return deviceTools;
  }

  svgComponent() {
    return SVG_COMPONENTS[this._deviceKey()];
  }

  _svgShadowRadius() {
    const key = this._deviceKey();

    if (key.includes('Framed') && key.includes('phone')) {
      return 52;
    }

    if (key.includes('Framed') && key.includes('tablet')) {
      return 42;
    }

    return 0;
  }

  renderStandardIcon(deviceName = this.deviceName) {
    return svgToIcon(DEVICE_STANDARD_ICONS[deviceName]);
  }

  orientations() {
    return ['portrait', 'landscape'];
  }

  _deviceKey(): string {
    if (this.deviceName === 'browser') {
      return 'browser';
    }

    if (this.deviceName === 'laptop') {
      return this.showBrowserBar ? 'laptopBrowserBar' : 'laptop';
    }

    let key = this.deviceName;
    key += upperFirst(this.isPortrait ? 'vertical' : 'horizontal');
    Device.styleFlags.forEach(flag => {
      if (this[flag]) {
        const name = flag.split('show')[1];
        key += upperFirst(name);
      }
    });
    return key;
  }

  _renderShadow(ctx: CanvasRenderingContext2D) {
    if (this.hovered) {
      const { width, height } = this;
      const w = width;
      const h = height;
      const x = -width / 2;
      const y = -height / 2;

      ctx.save();

      ctx.fillStyle = 'none';
      ctx.shadowBlur = 10;
      ctx.shadowColor = HOVER_SHADOW_COLOR;

      // For mobile devices, fit the hover border shadow to the device
      if (MOBILE_DEVICES.includes(this.deviceName)) {
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(x, y, w, h, this._svgShadowRadius());
        } else {
          ctx.rect(x, y, w, h);
        }
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.rect(x - 100, y - 100, w + 200, h + 200);
        ctx.rect(x, y, w, h);
        ctx.clip('evenodd');
        ctx.fillRect(x, y, w, h);
      }

      ctx.restore();
    }
  }

  minDimension({ resizingWidth = true }) {
    const { width, height } = this.deviceDimensions();
    return resizingWidth ? width : height;
  }

  maxDimension() {
    return Number.MAX_SAFE_INTEGER;
  }

  // When the size is set, reset the dimensions to the original size
  setSize() {
    const { width, height } = this.deviceDimensions();
    this.set({ width, height });
  }

  deviceDimensions(deviceName = this.deviceName) {
    return Device.deviceDimensions(deviceName, this.orientation);
  }

  // Static so that we can use it from the constructor
  static deviceDimensions(
    deviceName: string,
    orientation?: 'portrait' | 'landscape'
  ) {
    const deviceDims = DEVICE_DIMENSIONS[deviceName];
    if (!deviceDims) {
      return DEVICE_DIMENSIONS.phone;
    }

    let { width, height } = deviceDims;

    // For mobile devices, swap dimensions based on orientation
    if (MOBILE_DEVICES.includes(deviceName) && orientation === 'landscape') {
      width = deviceDims.height;
      height = deviceDims.width;
    }

    return { width, height };
  }

  get isPortrait() {
    return this.orientation === 'portrait';
  }

  get isTablet() {
    return this.deviceName === 'tablet';
  }

  get borderWidth() {
    switch (this.deviceName) {
      case 'phone':
        return 10;
      case 'tablet':
        return 20;
      case 'laptop':
      case 'browser':
        return 2;
      default:
        return 0;
    }
  }

  get borderRadius() {
    const { showFramed } = this;
    switch (this.deviceName) {
      case 'phone':
        return showFramed ? 44 : 0;
      case 'tablet':
        return showFramed ? 37 : 0;
      case 'browser':
        return { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 };
      case 'laptop':
      default:
        return 0;
    }
  }

  get deviceStatusBar() {
    const {
      borderWidth,
      width,
      height,
      deviceName,
      orientation,
      showFramed,
      showStatusBar,
    } = this;
    switch (this.deviceName) {
      case 'browser':
        return (
          <BrowserStatusBar
            x={borderWidth / 2}
            y={borderWidth / 2}
            width={width - borderWidth}
            height={52}
          />
        );
      case 'phone':
      case 'tablet':
      default:
        return (
          <MobileStatusBar
            width={width}
            height={height}
            device={deviceName as 'phone' | 'tablet'}
            orientation={orientation}
            notch={showFramed}
            statusBar={showStatusBar}
          />
        );
    }
  }

  get deviceComponent() {
    const {
      borderWidth,
      width,
      height,
      deviceStatusBar,
      showFramed,
      borderRadius,
    } = this;

    const deviceFill =
      this.fill && this.fill !== 'transparent' ? this.fill : 'white';

    switch (this.deviceName) {
      case 'phone':
      case 'tablet':
      case 'browser':
        return (
          <FlexColumn
            border={
              showFramed
                ? { lineWidth: borderWidth, color: 'black' }
                : 'transparent'
            }
            borderRadius={borderRadius}
            height={height}
            width={width}
            fill={deviceFill}
          >
            {deviceStatusBar}
          </FlexColumn>
        );
      case 'laptop':
        return (
          <Image
            src={svgToURL(
              this.svgComponent().replace(/DEVICE_FILL_COLOR/g, `${deviceFill}`)
            )}
            width={width}
            height={height}
          />
        );
      default:
        return null;
    }
  }

  _render(ctx: CanvasRenderingContext2D) {
    this._renderShadow(ctx);

    const { width, height, deviceComponent } = this;

    ctx.save();
    ctx.translate(-width / 2, -height / 2);

    withContext(
      {
        canvas: this.canvas,
        ctx,
        object: this,
        scale: 1,
      },
      () => {
        renderElement(<Rect>{deviceComponent}</Rect>);
      }
    );

    ctx.restore();
  }

  _set(keyOrPath: KeyPath, value: any): fabric.Object {
    if (keyOrPath === 'orientation') {
      this.updateOrientation(value);
    }
    if (keyOrPath === 'deviceName') {
      this.initializeDevice(value);
      this.controller.actionRerenderToolbar();
    }

    super._set(keyOrPath, value);

    switch (keyOrPath) {
      case 'deviceName':
      case 'showFramed':
      case 'showBrowserBar':
      case 'showStatusBar':
        this.layoutWithChildren();
        break;
    }

    return this;
  }

  // Margins for LayoutBehaviors inside the device.
  layoutMarginSides(): SpacingSidesStruct {
    const sx = this.scaleX;
    const sy = this.scaleX;
    const bw = this.borderWidth / 2;

    switch (this.deviceName) {
      case 'phone':
      case 'tablet':
        if (this.showStatusBar || this.showFramed) {
          return {
            top: (bw + 45) * sy,
            left: bw * sx,
            right: bw * sx,
            bottom: bw * sy,
          };
        } else {
          return {
            top: bw * sy,
            left: bw * sx,
            right: bw * sx,
            bottom: bw * sy,
          };
        }
      case 'laptop':
        if (this.showBrowserBar) {
          return {
            top: (27 + 42) * sy,
            left: 100 * sx,
            right: 100 * sx,
            bottom: 100 * sy,
          };
        } else {
          return {
            top: 27 * sy,
            left: 100 * sx,
            right: 100 * sx,
            bottom: 100 * sy,
          };
        }
      case 'browser':
        return { top: 52 * sy, left: 0, right: 0, bottom: 0 };
      default:
        return { top: 0, left: 0, right: 0, bottom: 0 };
    }
  }
}

export default Device;
